import type { Api } from "../api";
import { byCode, kindWords, modelWords, statusTone, termsWords, workLinks } from "../accounts";
import { empty, h, label, pill } from "../dom";
import { navigate } from "../nav";
import { words } from "../orders";
import type { Account, ModelRule, Page } from "../types";
import { checkField, crumbs } from "./shared";

// Accounts (accounts, orders.read; 0076): Tare's customers, suppliers and other parties, sorted by name. The list
// shows what each is, its status, Direct or Broker/Agent, and its open work; one party (/accounts/<code>) shows who
// it's Direct or Broker/Agent with (the customer + supplier rules, 0071), distributor and end customers, terms,
// plants, contacts, and links to its work on the other screens. Read only: terms are set through Claude, and
// onboarding is still to design.

const KINDS = ["ALL", "CUSTOMER", "SUPPLIER", "OTHER"];
const KIND_WORDS: Record<string, string> = { ALL: "Everyone", CUSTOMER: "Customers", SUPPLIER: "Suppliers", OTHER: "Others" };

const link = (code: string, name: string) => h("a", { href: `/accounts/${encodeURIComponent(code)}` }, name);

export async function accounts(api: Api, query: URLSearchParams): Promise<HTMLElement> {
  const party = (query.get("q") ?? "").slice(0, 100);
  const kind = KINDS.includes(query.get("kind") ?? "") ? query.get("kind")! : "ALL";
  const inactive = query.get("inactive") === "1";
  const cursor = /^[0-9]{1,6}$/.test(query.get("cursor") ?? "") ? query.get("cursor")! : undefined;
  const page = await api.tool<Page<Account>>("accounts", {
    kind, limit: 100, ...(party ? { party } : {}), ...(inactive ? { include_inactive: true } : {}), ...(cursor ? { cursor } : {}),
  });

  const qInput = h("input", { id: "q", type: "search", value: party, placeholder: "Name or code", maxlength: 100 });
  const kindSelect = h("select", { id: "kind" }, KINDS.map((k) => h("option", { value: k, selected: k === kind }, KIND_WORDS[k])));
  const { field: inactiveField, box: inactiveBox } = checkField("inactive", "Dormant and ended too", inactive);
  const go = () => navigate(`/accounts?${new URLSearchParams({ ...(qInput.value.trim() ? { q: qInput.value.trim() } : {}),
    ...(kindSelect.value !== "ALL" ? { kind: kindSelect.value } : {}), ...(inactiveBox.checked ? { inactive: "1" } : {}) })}`);
  const form = h("form", { class: "filters", role: "search" },
    h("div", { class: "field" }, h("label", { for: "q" }, "Search"), qInput),
    h("div", { class: "field" }, h("label", { for: "kind" }, "Show"), kindSelect),
    inactiveField,
    h("button", { type: "submit", class: "button" }, "Show"));
  form.addEventListener("submit", (e) => { e.preventDefault(); go(); });
  kindSelect.addEventListener("change", go);
  inactiveBox.addEventListener("change", go);

  return h("div", { class: "page" },
    h("div", { class: "stack-tight" },
      h("h1", { class: "title" }, "Accounts"),
      h("p", { class: "lede" }, page.rows.length
        ? `${page.rows.length}${page.next_cursor ? "+" : ""} ${kind === "ALL" ? (page.rows.length === 1 ? "account" : "accounts") : KIND_WORDS[kind].toLowerCase()}` +
          `${party ? ` matching “${party}”` : ""}.`
        : "No accounts match.")),
    form,
    page.rows.length
      ? h("div", { class: "panel scroll" }, h("table", { class: "tbl" },
          h("thead", null, h("tr", null,
            h("th", { scope: "col" }, "Name"), h("th", { scope: "col" }, "What"), h("th", { scope: "col" }, "Status"),
            h("th", { scope: "col" }, "Direct or Broker/Agent"), h("th", { scope: "col", class: "r" }, "Open orders"),
            h("th", { scope: "col", class: "r" }, "Open vendor POs"), h("th", { scope: "col" }, "Email"))),
          h("tbody", null, page.rows.map((a) => h("tr", null,
            h("td", { class: "strong" }, link(a.code, a.name)),
            h("td", null, kindWords(a.kinds)),
            h("td", null, pill(a.status.toLowerCase(), statusTone(a.status))),
            h("td", null, modelWords(a.model),
              (a.modelBySupplier?.length || a.modelByCustomer?.length)
                ? h("div", { class: "caption" }, `${(a.modelBySupplier ?? a.modelByCustomer ?? []).length} by supplier or customer`) : null),
            h("td", { class: "num r" }, a.openOrders ? String(a.openOrders) : ""),
            h("td", { class: "num r" }, a.openVendorPos ? String(a.openVendorPos) : ""),
            h("td", null, a.emailRule ? h("span", { class: "caption" }, a.emailRule) : h("span", { class: "muted" }, "open")))))))
      : empty("Nothing here", "Try part of the name, or Everyone."),
    page.next_cursor
      ? h("div", null, h("a", { class: "button", href: `/accounts?${new URLSearchParams({ ...(party ? { q: party } : {}),
          ...(kind !== "ALL" ? { kind } : {}), ...(inactive ? { inactive: "1" } : {}), cursor: page.next_cursor })}` }, "Next 100"))
      : null);
}

function rulesTable(title: string, rules: ModelRule[], other: "supplier" | "customer"): HTMLElement {
  return h("section", { class: "stack-tight" }, label(title),
    h("div", { class: "scroll" }, h("table", { class: "tbl" },
      h("thead", null, h("tr", null, h("th", { scope: "col" }, other === "supplier" ? "Supplier" : "Customer"), h("th", { scope: "col" }, "Model"),
        h("th", { scope: "col" }, "From"), h("th", { scope: "col" }, "Note"))),
      h("tbody", null, rules.map((r) => {
        const code = other === "supplier" ? r.supplier : r.customer;
        const name = (other === "supplier" ? r.supplierName : r.customerName) ?? code ?? "";
        return h("tr", null, h("td", null, code ? link(code, name) : name), h("td", null, modelWords(r.model)),
          h("td", { class: "num" }, r.from ?? ""), h("td", null, r.note ?? ""));
      })))));
}

export async function account(api: Api, code: string): Promise<HTMLElement> {
  const page = await api.tool<Page<Account>>("accounts", { party: code, include_inactive: true, limit: 50 });
  const a = byCode(page.rows, code);
  if (!a) return h("div", { class: "page" }, crumbs({ text: "Accounts", href: "/accounts" }), h("p", { class: "lede" }, "There's no such account."));
  const parties = (title: string, list?: { code: string; name: string }[]) => list?.length
    ? h("div", { class: "stack-tight" }, label(title), h("p", null, list.flatMap((p, i) => [i ? ", " : "", link(p.code, p.name)])))
    : null;
  const t = a.terms;

  return h("div", { class: "page" },
    crumbs({ text: "Accounts", href: "/accounts" }, { text: a.name }),
    h("div", { class: "stack-tight" },
      h("div", null, pill(a.status.toLowerCase(), statusTone(a.status)), " ", label(kindWords(a.kinds))),
      h("h1", { class: "title" }, a.name),
      a.legalName && a.legalName !== a.name ? h("p", { class: "caption" }, a.legalName) : null,
      h("div", { class: "row-act" }, workLinks(a).map((l) => h("a", { class: "button button-small", href: l.href }, l.text)))),
    h("div", { class: "layout" },
      h("div", { class: "main stack" },
        a.modelBySupplier?.length ? rulesTable(`Direct or Broker/Agent by supplier (otherwise ${modelWords(a.model) || "the customer's setting"})`, a.modelBySupplier, "supplier") : null,
        a.modelByCustomer?.length ? rulesTable("Direct or Broker/Agent by customer", a.modelByCustomer, "customer") : null,
        parties("Buys through", a.buysThrough), parties("End customers", a.endCustomers),
        parties("Owns", a.owns),
        a.plants?.length
          ? h("div", { class: "stack-tight" }, label("Plants"), h("ul", { class: "plain" },
              a.plants.map((p) => h("li", null, p.name, p.city || p.state ? h("span", { class: "caption" }, ` · ${[p.city, p.state].filter(Boolean).join(", ")}`) : null))))
          : null,
        h("div", { class: "stack-tight" }, label("Contacts"),
          a.contacts?.length
            ? h("ul", { class: "cards" }, a.contacts.map((c) => h("li", { class: "card" },
                h("span", { class: "strong" }, c.name, c.primary ? " (primary)" : ""), c.title ? h("span", { class: "caption" }, c.title) : null,
                c.email ? h("span", { class: "num" }, c.email) : null, c.phone ? h("span", { class: "num" }, c.phone) : null)))
            : h("p", { class: "muted" }, "None recorded."))),
      h("aside", { class: "side" },
        h("div", { class: "panel facts" },
          label("Code"), h("span", { class: "num" }, a.code),
          a.kinds.includes("CUSTOMER") ? [label("Direct or Broker/Agent"), h("span", null, modelWords(a.model) || "—")] : null,
          a.ownedBy ? [label("Owned by"), link(a.ownedBy.code, a.ownedBy.name)] : null,
          a.roles?.length ? [label("Roles"), h("span", null, a.roles.map((r) => words(r)).join(", "))] : null,
          label("Email"), h("span", null, a.emailRule ?? "Open: emails go to review or out as the send policy says"),
          label("Open work"), h("span", null, `${a.openOrders ?? 0} orders, ${a.openVendorPos ?? 0} vendor POs`)),
        t
          ? h("div", { class: "panel facts" },
              label("Terms"), h("span", null, termsWords(t) || "—"),
              t.freight || t.fob ? [label("Freight"), h("span", null, [t.freight, t.fob ? `FOB ${t.fob}` : null].filter(Boolean).join(" · "))] : null,
              t.leadTimeDays ? [label("Lead time"), h("span", null, `${t.leadTimeDays} days`)] : null,
              t.status ? [label("Agreed"), h("span", null, `${words(t.status)}${t.confirmedOn ? `, ${t.confirmedOn}` : ""}`)] : null,
              t.perCustomer ? [label("Per customer"), h("span", null, `${t.perCustomer} customer-specific ${t.perCustomer === 1 ? "set" : "sets"}`)] : null)
          : null)));
}
